// src/components/SubscriptionBilling.jsx
import React, { useEffect, useRef, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { Link } from "react-router-dom";
import { axiosApi } from "config";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "store/slices/auth";

import SubscriptionPlans from "./stripe/SubscriptionPlans"; // Import your plans component

const SubscriptionBilling = () => {
  const dispatch = useDispatch();
  const [editNumber, setEditNumber] = useState(false);
  const [editFirstPlan, setEditFirstPlan] = useState(false);
  const [editSecondPlan, setEditSecondPlan] = useState(false);

  const firstPlanRef = useRef(null);
  const numberRef = useRef(null);
  const secondPlanRef = useRef(null);

  const [subscription, setSubscription] = useState(null);
  const [firstPlan, setFirstPlan] = useState("");
  const [number, setNumber] = useState("•••• •••• •••• 7438");
  const [secondPlan, setSecondPlan] = useState("");

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axiosApi.get("/api/subscription/details", {
          withCredentials: true,
        });
        dispatch(updateUser(response.data));
        setSubscription(response.data.subscription);
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        toast.error("Failed to load subscription details.");
      }
    };
    fetchSubscriptionDetails();
  }, []);

  // Populate dynamic fields when subscription data is available
  useEffect(() => {
    if (subscription) {
      setFirstPlan(subscription.plan || "");
      setNumber(subscription.paymentMethod || "•••• •••• •••• 7438");
      setSecondPlan(subscription.plan || "");
    }
  }, [subscription]);

  useEffect(() => {
    if (editNumber && numberRef.current) numberRef.current.focus();
  }, [editNumber]);

  useEffect(() => {
    if (editFirstPlan && firstPlanRef.current) firstPlanRef.current.focus();
  }, [editFirstPlan]);

  useEffect(() => {
    if (editSecondPlan && secondPlanRef.current) secondPlanRef.current.focus();
  }, [editSecondPlan]);

  const handleNumberEdit = () => setEditNumber(!editNumber);
  const handleFirstPlanEdit = () => setEditFirstPlan(!editFirstPlan);
  const handleSecondPlanEdit = () => setEditSecondPlan(!editSecondPlan);

  // If no subscription exists, show SubscriptionPlans component
  if (!subscription) {
    return <SubscriptionPlans />;
  }

  return (
    <div className="mt-4">
      <Link to="/main/profile">
        <div className="flex items-center gap-4 font-bold text-purple-800 hover:text-purple-500">
          <MdArrowBack className="flex h-4 w-4" />
          <p>Back to Settings</p>
        </div>
      </Link>
      <div className="mt-4">
        {/* Subscription Plan Section */}
        <div className="flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md">
          <h4>Subscription Plan</h4>
          <div className="mt-4 flex w-full justify-start gap-10 pb-2">
            <div className="w-full max-w-[300px]">
              <p className="font-medium text-gray-800">Current plan details</p>
              <input
                type="text"
                ref={firstPlanRef}
                className={`mt-2 rounded-md py-2 ${
                  !editFirstPlan ? "bg-purple-50" : "!bg-white"
                } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                disabled={!editFirstPlan}
                value={firstPlan}
                onChange={(e) => setFirstPlan(e.target.value)}
                aria-label="FirstPlan"
              />
            </div>
            <button
              className="flex font-bold text-purple-800 hover:text-purple-500"
              onClick={handleFirstPlanEdit}
            >
              {editFirstPlan ? "Save" : "Change Plan"}
            </button>
          </div>
        </div>

        {/* Payment Information Section */}
        <div className="mt-4 flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md">
          <h4>Payment Information</h4>
          <div className="mt-4 flex w-full justify-start gap-10 pb-2">
            <div className="w-full max-w-[300px]">
              <p className="font-medium text-gray-800">Payment Method</p>
              <input
                type="text"
                ref={numberRef}
                className={`mt-2 rounded-md py-2 ${
                  !editNumber ? "bg-purple-50" : "!bg-white"
                } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
                disabled={!editNumber}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                aria-label="Number"
              />
            </div>
            <button
              className="flex font-bold text-purple-800 hover:text-purple-500"
              onClick={handleNumberEdit}
            >
              {editNumber ? "Save" : "Change"}
            </button>
          </div>
        </div>

        {/* Billing History Section */}
        <div className="mt-4 flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md">
          <h4>Billing History</h4>
          <div className="mt-4 flex w-full justify-start gap-10 border-b border-gray-200 pb-4">
            <div className="w-full max-w-[300px]">
              <p className="font-medium text-gray-800">Past Invoices</p>
            </div>
            <button className="flex font-bold text-purple-800 hover:text-purple-500">
              Download
            </button>
          </div>
          <div className="mt-4 flex w-full justify-start gap-10 pb-2">
            <div className="w-full max-w-[300px]">
              <p className="font-medium text-gray-800">Current plan details</p>
            </div>
            <input
              type="text"
              ref={secondPlanRef}
              className={`mt-2 rounded-md py-2 ${
                !editSecondPlan ? "bg-purple-50" : "!bg-white"
              } w-full min-w-[300px] focus:outline-none focus:ring-1 focus:ring-purple-500`}
              disabled={!editSecondPlan}
              value={secondPlan}
              onChange={(e) => setSecondPlan(e.target.value)}
              aria-label="SecondPlan"
            />
            <button
              className="flex font-bold text-purple-800 hover:text-purple-500"
              onClick={handleSecondPlanEdit}
            >
              {editSecondPlan ? "Save" : "Change Plan"}
            </button>
          </div>
        </div>

        {/* Subscription Options Section */}
        <div className="mt-4 flex flex-col rounded-lg bg-gradient-to-b from-gray-50 to-purple-50 p-4 text-left shadow-md">
          <h4>Subscription Options</h4>
          <div className="mt-6 flex gap-4">
            <button className="font-bold text-purple-800 hover:text-purple-500">
              Cancel Subscription
            </button>
            <button className="flex items-center rounded-full border-2 border-purple-800 px-4 py-2 text-purple-800 transition duration-150 hover:bg-purple-50 hover:text-purple-500">
              Pause Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBilling;
